import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { fetchBestOfWolt } from 'apis/merchant-gateway/best-of-wolt/api';

import { RelativeMonth } from '../types';

type UseBestOfWoltDataProps = {
  venueId: string;
  relativeMonth: RelativeMonth;
  venueTimezone: string;
  enabled?: boolean;
};

export const useBestOfWoltData = ({
  venueId,
  relativeMonth,
  venueTimezone,
  enabled = true,
}: UseBestOfWoltDataProps) => {
  const relativeDate: DateTime = useMemo(() => {
    const now = DateTime.now().setZone(venueTimezone);
    switch (relativeMonth) {
      case RelativeMonth.NEXT_MONTH:
        return now.plus({ month: 1 });
      case RelativeMonth.THIS_MONTH:
        return now;
      case RelativeMonth.LAST_MONTH:
        return now.minus({ month: 1 });
      default:
        const _exhaustiveCheck: never = relativeMonth;
        throw new Error(`Unhandled relativeMonth: ${_exhaustiveCheck}`);
    }
  }, [relativeMonth, venueTimezone]);

  const {
    data = null,
    isLoading,
    isError,
  } = useQuery(
    [`/merchant-analytics-api/bow/venues/${venueId}/tracking`, relativeDate.month, relativeDate.year],
    () =>
      fetchBestOfWolt({
        venue_id: venueId,
        month: relativeDate.month,
        year: relativeDate.year,
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled,
    },
  );

  return {
    data,
    isLoading,
    isError,
    relativeDate,
  };
};
